import { ApolloLink, Observable } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { createConsumer } from '@rails/actioncable'
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink'
import logger from 'utils/logger'

import { baseWsUrl, buildWsUrl } from './wsHandshake'

// The ws-handshake token is consume-once server-side, so every socket open
// needs a token of its own. We pass `createConsumer` a *synchronous* URL
// factory, which @rails/actioncable re-invokes on every open via the
// `consumer.url` getter — so reconnects get a fresh token instead of replaying
// a spent one. The factory must be sync (`createWebSocketURL` calls
// `url()` inline), and minting is a fetch, so we keep one token minted ahead:
// `nextWsUrl()` hands out the ready one and immediately arms a replacement.
//
// On token refresh, `reconnectTicketedCable()` deliberately leaves a healthy
// cable ALONE (ME-847). A JWT refresh cannot invalidate an open socket: mammoth
// authenticates once in `ApplicationCable::Connection#connect` and identifies
// the connection by `current_user` from then on — it never re-reads the JWT.
// Nothing about a live connection goes stale when the token rotates.
//
// It used to tear down and rebuild, which was correct before the factory above
// existed: the cable was built as `createConsumer(url)` with the token baked
// into an immutable string, so a new token meant a new consumer. Now that the
// URL is a factory re-invoked per open, the rebuild is pure downside — the
// subscription collection belongs to the Consumer, so replacing it stranded
// every live Apollo subscription on a dead transport with no `error`/`complete`
// delivered to the observer. Apollo went on believing it was subscribed and
// advocates silently stopped receiving messages until they reloaded.
//
// The dynamic `getToken` supplied to `configureTicketedCable({ getToken })` is
// preserved across teardowns, so any later mint reads the up-to-date JWT from
// storage rather than a stale snapshot. Reconnects driven by the factory do NOT
// lose subscriptions — ActionCable's own `subscriptions.reload()` restores them
// on `welcome`, which is what makes leaving the socket alone safe even if it
// drops later.

let cable = null
let actionCableLink = null
let pendingInit = null
let currentTokenGetter = () => ''

// Bumped on every disconnect/reconnect so an in-flight init can detect it was
// superseded and abort instead of writing back to module state.
let generation = 0

// `readyWsUrl` holds a minted, unspent token for the next socket open.
// `lastWsUrl` is the fallback when a refill hasn't landed yet — the server
// rejects it, which prompts another retry that the refill will have armed.
let readyWsUrl = null
let lastWsUrl = null
let refillInFlight = null

const SUPERSEDED_MESSAGE = 'cable init superseded'

function isSupersededError(err) {
  return err?.message === SUPERSEDED_MESSAGE
}

// Same guard as the monitoring link in apollo/client.js: RUM drops actions
// silently until it has a session, so an unguarded report is invisible rather
// than merely early. Subscriptions have no RUM coverage otherwise —
// `monitoringLink` returns early for them.
function reportRumAction(name, attributes) {
  const ddContext = window.DD_RUM?.getInternalContext?.()
  if (ddContext?.session_id) datadogRum.addAction(name, attributes)
}

// The channels ActionCableLink created on the current consumer, i.e. what a
// teardown would take with it.
//
// `Subscriptions#subscriptions` is actioncable's backing array, not public API,
// so returns null rather than 0 when it can't be read — a missing internal and
// an empty cable are different facts, and collapsing them would leave the
// warning below permanently silent.
function liveSubscriptionCount() {
  if (!cable) return 0
  const channels = cable.subscriptions?.subscriptions
  return Array.isArray(channels) ? channels.length : null
}

// Mints a handshake token and arms it for the next socket open. Concurrent
// callers (a disconnect and an open landing together) share one mint so we
// don't orphan tokens in Redis until their TTL. Rejections are the caller's to
// handle — `initActionCableLink` surfaces them, `refillInBackground` logs them.
//
// The armed token carries the endpoint's 300s TTL while a connection can stay
// open for hours. A clean drop re-mints via the `disconnected` callback, so the
// token is fresh. A silent drop (laptop sleep, half-open socket) has no close
// event and so no callback: the reopen presents an expired token and is
// rejected once, and the refill that open triggers makes the next retry work.
function refillWsUrl() {
  if (refillInFlight) return refillInFlight

  const myGeneration = generation
  const p = (async () => {
    const url = await buildWsUrl(currentTokenGetter() || '')
    // A mint started by a cable that has since been torn down must not arm the
    // cable that replaced it — same hazard `initActionCableLink` guards against.
    if (myGeneration !== generation) return null
    readyWsUrl = url
    return url
  })()

  refillInFlight = p
  p.catch(() => {}).finally(() => {
    if (refillInFlight === p) refillInFlight = null
  })

  return p
}

// Fire-and-forget refill for the reconnect paths, which have no caller to
// return a rejection to.
function refillInBackground() {
  refillWsUrl().catch(err => {
    logger.logWarning('ws-handshake token refill failed', {}, err)
  })
}

// Passed to `createConsumer` and invoked by @rails/actioncable on every socket
// open. Synchronous by necessity, so it hands out the pre-minted token and
// arms the next one in the background.
function nextWsUrl() {
  const url = readyWsUrl ?? lastWsUrl
  readyWsUrl = null
  refillInBackground()

  if (!url) {
    // Reachable when the consumer outlives a teardown that cleared both slots.
    // Returning a falsy URL would throw inside `Connection#open`, and that
    // throw escapes `ConnectionMonitor.poll` before it reschedules itself —
    // killing reconnects for good. A token-less URL is rejected cleanly
    // instead, and the retry picks up whatever the refill above armed.
    return baseWsUrl()
  }

  lastWsUrl = url
  return url
}

function initActionCableLink() {
  if (actionCableLink) return Promise.resolve(actionCableLink)
  if (pendingInit) return pendingInit

  const myGeneration = ++generation
  const p = (async () => {
    // Arm the first token before handing the factory over, so the initial open
    // never has to fall back.
    const armed = await refillWsUrl()
    if (myGeneration !== generation || !armed) {
      throw new Error(SUPERSEDED_MESSAGE)
    }
    cable = createConsumer(nextWsUrl)
    actionCableLink = new ActionCableLink({
      cable,
      channelName: 'GraphQLChannel',
      // Re-mint on socket close so a fresh token is ready before the monitor
      // reopens (~6s later). ActionCableLink registers this per subscription
      // inside cable.subscriptions.create, and notifyAll maps over them, so it
      // fires once per active channel — zero times when none are subscribed —
      // and not at all for a socket that never reached OPEN. Hence the refill
      // inside nextWsUrl is the load-bearing part and this is only a shortcut;
      // refillWsUrl dedupes the N-channel case into a single mint.
      callbacks: {
        disconnected: () => {
          // `tearDown` bumps `generation` before calling `cable.disconnect()`,
          // so a close we caused ourselves is distinguishable from a real drop.
          // Minting for a cable being discarded wastes a request — and after
          // sign-out the JWT getter returns '', so it would 401 and log noise.
          if (myGeneration !== generation) return
          refillInBackground()
        }
      }
    })
    return actionCableLink
  })()

  pendingInit = p
  // Clear pendingInit on failure so a later subscriber can retry, but only
  // if pendingInit still points to *this* promise. A stale rejection from a
  // superseded init must not null out a newer in-flight pendingInit.
  p.catch(err => {
    // "superseded" errors are expected — a newer init took over. Don't log.
    if (!isSupersededError(err)) {
      logger.logWarning('ws-handshake cable init failed', {}, err)
    }
    if (pendingInit === p) pendingInit = null
  })

  return p
}

function tearDown(reason) {
  const discarded = liveSubscriptionCount()
  reportRumAction('ws-handshake cable discarded', {
    reason,
    subscriptions: discarded
  })
  // Discarding live channels is correct on sign-out — the identity changes, so
  // the socket and any unspent token must go. Under any other reason it means a
  // cable with live subscriptions was thrown away, which is the regression this
  // guards against. Unreachable while a healthy cable is preserved; it exists
  // because the failure is otherwise completely silent.
  if (discarded === null) {
    logger.logWarning('ws-handshake subscription count unavailable', { reason })
  } else if (discarded > 0 && reason !== 'signout') {
    logger.logWarning('ws-handshake rebuild discarded live subscriptions', {
      reason,
      subscriptions: discarded
    })
  }
  // Bump generation FIRST so any in-flight init sees the mismatch and aborts
  // before writing back to module-level cable/actionCableLink.
  generation++
  try {
    cable?.disconnect()
  } catch (err) {
    logger.logWarning('ws-handshake cable disconnect failed', {}, err)
  }
  cable = null
  actionCableLink = null
  pendingInit = null
  // Drop any unspent token with the cable it was minted for — a new cable
  // (post sign-out or JWT refresh) must never present the old session's token.
  readyWsUrl = null
  lastWsUrl = null
  refillInFlight = null
  // NOTE: currentTokenGetter is NOT reset here. The dynamic getter supplied
  // by configureTicketedCable({ getToken }) reads from storage on every call,
  // so tearing down + rebuilding will pick up whatever token is current at
  // the time of the next initActionCableLink call.
}

export function configureTicketedCable({ getToken }) {
  currentTokenGetter = getToken
}

export function reconnectTicketedCable() {
  // Intentionally a no-op when a cable already exists — see the note at the top
  // of this file. Don't "fix" this back into a rebuild: that is ME-847. A
  // non-null `cable` always has a running ConnectionMonitor (only `tearDown`
  // clears it), so if its socket is genuinely down the monitor is already
  // reopening it, and the URL factory mints a fresh token for that open.
  // Both, not just `cable`: `initActionCableLink` assigns the consumer and then
  // the link, and its catch clears only `pendingInit`. So a throw from the
  // `ActionCableLink` constructor leaves a consumer with no link and no
  // subscriptions, and preserving that would strand an open socket that a later
  // init reassigns past without disconnecting. Falling through tears it down and
  // rebuilds, which is what that state needs.
  if (cable && actionCableLink) {
    reportRumAction('ws-handshake cable preserved', {
      subscriptions: liveSubscriptionCount()
    })
    return
  }
  // No cable to preserve. Supersede any in-flight init (which may have been
  // armed with the pre-refresh JWT) and eagerly warm a new one, so subsequent
  // Apollo requests find a connected cable. Errors are already logged inside
  // initActionCableLink's catch handler — we still attach a no-op catch here to
  // prevent an unhandled rejection.
  tearDown('refresh')
  initActionCableLink().catch(() => {})
}

export function disconnectTicketedCable() {
  tearDown('signout')
}

export const ticketedActionCableLink = new ApolloLink(
  (operation, forward) =>
    new Observable(observer => {
      let subscription
      // If the caller unsubscribes before initActionCableLink() resolves, we
      // must skip creating the inner subscription — otherwise a live cable
      // subscription would be built after teardown with no way to cancel it.
      let cancelled = false
      initActionCableLink()
        .then(link => {
          if (cancelled) return
          subscription = link.request(operation, forward).subscribe(observer)
        })
        .catch(err => {
          if (cancelled) return
          // "cable init superseded" is an internal coordination signal
          // (a newer init took over via reconnect/disconnect), not a real
          // subscription error. Swallow it here — the caller will get a
          // fresh subscription attempt on the next Apollo request().
          if (isSupersededError(err)) return
          observer.error(err)
        })
      return () => {
        cancelled = true
        subscription?.unsubscribe()
      }
    })
)
